import { supabase } from '../supabase';
import { Species } from '../utils/types';

export interface ReceiverAccessScope {
  id?: number;
  season: string;
  species_id: number;
  species?: Species;
}

export interface ReceiverAccessUser {
  id: number;
  user_id: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  roles?: string[];
  is_active: boolean;
  created_at: string;
  scopes: ReceiverAccessScope[];
}

export async function getReceiverAccessUsers(
  exporterReceiverId: number
): Promise<ReceiverAccessUser[]> {
  const { data, error } = await supabase.rpc('get_receiver_access_users', {
    p_exporter_receiver_id: exporterReceiverId,
  });

  if (error) throw error;
  return ((data as { users?: ReceiverAccessUser[] } | null)?.users ||
    []) as ReceiverAccessUser[];
}

export async function getReceiverAccessCountsForExporter(
  exporterId: number
): Promise<Record<number, number>> {
  const { data, error } = await supabase.rpc(
    'get_receiver_access_counts_for_exporter',
    {
      p_exporter_id: exporterId,
    }
  );

  if (error) throw error;

  const rows =
    ((data as { counts?: { exporter_receiver_id: number; user_count: number }[] } | null)
      ?.counts || []) as { exporter_receiver_id: number; user_count: number }[];

  return rows.reduce<Record<number, number>>((acc, row) => {
    acc[Number(row.exporter_receiver_id)] = Number(row.user_count) || 0;
    return acc;
  }, {});
}

export async function setReceiverAccessActive(
  accessId: number,
  isActive: boolean
) {
  const { data, error } = await supabase.rpc('set_receiver_access_active', {
    p_access_id: accessId,
    p_is_active: isActive,
  });

  if (error) throw error;
  return data;
}

export async function updateReceiverAccessScopes(
  accessId: number,
  scopes: ReceiverAccessScope[]
) {
  const { data, error } = await supabase.rpc('set_receiver_access_scopes', {
    p_access_id: accessId,
    p_scopes: scopes.map((scope) => ({
      season: scope.season,
      species_id: scope.species_id,
    })),
  });

  if (error) throw error;
  return data;
}

export async function inviteReceiverUser(params: {
  exporterReceiverId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  scopes: ReceiverAccessScope[];
}) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-receiver-user`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        exporter_receiver_id: params.exporterReceiverId,
        email: params.email,
        first_name: params.firstName,
        last_name: params.lastName,
        scopes: params.scopes.map((scope) => ({
          season: scope.season,
          species_id: scope.species_id,
        })),
      }),
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Could not invite receiver user');
  }

  return payload;
}
